import { makeStyles } from '@material-ui/core'

// JSS shared between UtilityPanel and UtilityPanelToolbar. Style baseline
// mirrors aiApp/Assistant/Attachments/AttachmentPanel so a future shared
// extraction is straightforward.
export const useUtilityPanelStyles = makeStyles((theme) => ({
  panelWrapper: {
    height: '100%',
    minHeight: 0,
    minWidth: 0,
    display: 'flex',
    position: 'relative',
    overflow: 'hidden',
    transition: 'flex 0.2s ease',
    animation: '$slideIn 0.2s ease-out'
  },
  '@keyframes slideIn': {
    from: { opacity: 0, transform: 'translateX(40px)' },
    to: { opacity: 1, transform: 'translateX(0)' }
  },
  resizeHandle: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: 6,
    height: '100%',
    cursor: 'col-resize',
    zIndex: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover $resizeIndicator': { opacity: 1 }
  },
  resizeIndicator: {
    width: 3,
    height: 40,
    borderRadius: 2,
    backgroundColor: 'var(--color-ai-400, #90caf9)',
    opacity: 0,
    transition: 'opacity 150ms ease'
  },
  resizeHandleActive: {
    '& $resizeIndicator': {
      opacity: 1,
      backgroundColor: 'var(--color-ai-500, #42a5f5)'
    }
  },
  panel: {
    height: '100%',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'var(--opal-surface-default, #fff)',
    borderLeft: `1px solid ${theme.palette.divider}`,
    boxShadow: '-2px 0 4px rgba(0, 0, 0, 0.06)',
    overflow: 'hidden',
    position: 'relative',
    isolation: 'isolate'
  },
  // Pulse the left edge while a tool reports it's busy. Ports the
  // panelPulse keyframe pattern from the old ResearchAgentTray so the
  // "investigation in progress" cue survives the rework. Driven by the
  // panel host (not the tool) so any future tool can opt in via the
  // same `busy` prop / context flag without re-implementing the effect.
  panelBusy: {
    animation: '$borderPulse 2.4s ease-in-out infinite'
  },
  '@keyframes borderPulse': {
    '0%, 100%': {
      borderLeftColor: 'var(--color-ai-300)',
      boxShadow:
        '-2px 0 4px rgba(0, 0, 0, 0.06), ' +
        '0 0 14px -2px color-mix(in srgb, var(--color-ai-500) 25%, transparent)'
    },
    '50%': {
      borderLeftColor: 'var(--color-ai-500)',
      boxShadow:
        '-2px 0 4px rgba(0, 0, 0, 0.06), ' +
        '0 0 28px 0 color-mix(in srgb, var(--color-ai-500) 55%, transparent)'
    }
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '12px 14px',
    minHeight: 52,
    borderBottom: `1px solid ${theme.palette.divider}`,
    flexShrink: 0
  },
  headerIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: 28,
    height: 28,
    borderRadius: 6,
    background: 'linear-gradient(135deg, var(--color-ai-500) 0%, var(--color-ai-600) 100%)',
    color: 'var(--color-contrast-ai-500)'
  },
  // Neutral icon chip for non-AI exclusive tools (allocation / trend
  // / scatter when opened ad-hoc via right-click). The AI-gradient
  // chip above is reserved for Research.
  headerIconNeutral: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: 28,
    height: 28,
    borderRadius: 6,
    background: 'var(--color-neutral-100, #f3f4f6)',
    color: 'var(--color-neutral-700, #555)'
  },
  headerText: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column'
  },
  eyebrow: {
    fontSize: 10,
    fontWeight: 'var(--font-weight-bold, 700)',
    textTransform: 'uppercase',
    letterSpacing: 0.7,
    color: 'var(--color-ai-700)'
  },
  headerTitle: {
    fontSize: 14,
    fontWeight: 'var(--font-weight-semibold, 600)',
    color: 'var(--color-neutral-900, #111827)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  headerSubject: {
    fontSize: 11,
    color: 'var(--color-neutral-500, #6b7280)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  subjectButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '2px 6px 2px 0',
    margin: '-2px 0 0 -2px',
    background: 'transparent',
    border: '1px solid transparent',
    borderRadius: 4,
    cursor: 'pointer',
    color: 'var(--color-neutral-900, #111827)',
    fontFamily: 'inherit',
    fontSize: 14,
    fontWeight: 'var(--font-weight-semibold, 600)',
    lineHeight: 1.4,
    textAlign: 'left',
    transition: 'background-color 120ms ease, border-color 120ms ease',
    '&:hover': {
      background: 'var(--theme-color-surface-d2, #e8e8e8)'
    },
    '&:focus-visible': {
      outline: '2px solid var(--color-primary-400, #3a83f5)',
      outlineOffset: 1
    }
  },
  subjectName: {
    paddingLeft: 6,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0
  },
  subjectChevron: {
    fontSize: 10,
    opacity: 0.7,
    flexShrink: 0
  },
  body: {
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column'
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '4px 8px',
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: 'var(--color-neutral-050, #f9fafb)',
    flexShrink: 0,
    minHeight: 36
  },
  toolbarLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 2
  },
  toolbarRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 4
  },
  layoutButton: {
    padding: 5,
    borderRadius: 4,
    border: '1px solid transparent',
    '&:hover': {
      backgroundColor: theme.palette.action.hover
    }
  },
  layoutButtonActive: {
    backgroundColor: 'var(--color-ai-050, rgba(25, 118, 210, 0.08))',
    border: '1px solid var(--color-ai-300, rgba(25, 118, 210, 0.3))'
  }
}))
